import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, HardHat, Warehouse, Tractor, Truck, Snowflake, Factory } from 'lucide-react';
import { Button } from './ui/button';

const industriesDropdown = [
  { label: 'Construction', path: '/industries/construction', icon: HardHat },
  { label: 'Warehouse & Fulfillment', path: '/industries/warehouse', icon: Warehouse },
  { label: 'Food Processing', path: '/industries/food-processing', icon: Factory },
  { label: 'Agriculture', path: '/industries/agriculture', icon: Tractor },
  { label: 'Transportation', path: '/industries/transportation', icon: Truck },
  { label: 'Cold Storage', path: '/industries/cold-storage', icon: Snowflake },
];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIndustriesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isIndustryActive = location.pathname.startsWith('/industries');

  const navTo = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setMobileIndustriesOpen(false);
    setIndustriesOpen(false);
  };

  const NavLink = ({ path, label }) => (
    <button
      onClick={() => navTo(path)}
      className={`font-semibold text-base transition-all duration-300 relative whitespace-nowrap ${
        isActive(path) ? 'text-[#003366]' : 'text-gray-700 hover:text-[#003366]'
      }`}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {label}
      {isActive(path) && <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#003366]"></span>}
    </button>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg py-2' : 'bg-white py-3'
      }`}
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <div
          className="flex items-center cursor-pointer transition-transform duration-300 hover:scale-105"
          onClick={() => navTo('/')}
          data-testid="logo"
          aria-label="WorkNation Labour Ltd - Home"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_4a6f89fc-3f48-4a79-a99d-ae58bf14ecd6/artifacts/dqrx4d3s_Image%202025-09-15%20at%207.24%E2%80%AFAM.jpg"
            alt="WorkNation Labour Ltd Logo"
            className="h-14 w-auto"
            loading="eager"
          />
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-6" role="navigation" aria-label="Main navigation">
          <NavLink path="/" label="Home" />
          <NavLink path="/employers" label="Employers" />
          <NavLink path="/job-seekers" label="Job Seekers" />

          {/* Industries Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIndustriesOpen(!industriesOpen)}
              onMouseEnter={() => setIndustriesOpen(true)}
              className={`font-semibold text-base transition-all duration-300 flex items-center whitespace-nowrap ${
                isIndustryActive ? 'text-[#003366]' : 'text-gray-700 hover:text-[#003366]'
              }`}
              aria-expanded={industriesOpen}
              aria-haspopup="true"
              data-testid="nav-industries"
            >
              Industries
              <ChevronDown className={`ml-1 transition-transform duration-200 ${industriesOpen ? 'rotate-180' : ''}`} size={16} />
              {isIndustryActive && <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#003366]"></span>}
            </button>

            {industriesOpen && (
              <div
                className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50"
                onMouseLeave={() => setIndustriesOpen(false)}
              >
                {industriesDropdown.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navTo(item.path)}
                    className={`w-full flex items-center px-4 py-3 text-left transition-colors duration-200 ${
                      isActive(item.path) ? 'bg-blue-50 text-[#003366]' : 'text-gray-700 hover:bg-gray-50 hover:text-[#003366]'
                    }`}
                    data-testid={`nav-industry-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon size={18} className="mr-3 flex-shrink-0" aria-hidden="true" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <NavLink path="/about" label="About" />
          <NavLink path="/faq" label="FAQ" />
          <NavLink path="/blog" label="Blog" />

          <Button
            onClick={() => navTo('/employers')}
            className="bg-[#003366] hover:bg-[#004488] text-white px-5 py-2 transition-all duration-300 transform hover:scale-105 text-sm"
            data-testid="nav-contact-btn"
          >
            Get Workers Now
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden text-[#003366] p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
          data-testid="mobile-menu-toggle"
        >
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <nav className="lg:hidden bg-white border-t border-gray-200 py-4 px-6 space-y-1 shadow-lg" role="navigation" aria-label="Mobile navigation">
          {[
            { path: '/', label: 'Home' },
            { path: '/employers', label: 'Employers' },
            { path: '/job-seekers', label: 'Job Seekers' },
          ].map((item) => (
            <button
              key={item.path}
              onClick={() => navTo(item.path)}
              className={`block w-full text-left font-semibold text-lg py-3 px-2 rounded ${
                isActive(item.path) ? 'text-[#003366] bg-blue-50' : 'text-gray-700'
              }`}
            >
              {item.label}
            </button>
          ))}

          {/* Mobile Industries Accordion */}
          <div>
            <button
              onClick={() => setMobileIndustriesOpen(!mobileIndustriesOpen)}
              className={`w-full flex items-center justify-between font-semibold text-lg py-3 px-2 rounded ${
                isIndustryActive ? 'text-[#003366] bg-blue-50' : 'text-gray-700'
              }`}
            >
              Industries
              <ChevronDown className={`transition-transform duration-200 ${mobileIndustriesOpen ? 'rotate-180' : ''}`} size={18} />
            </button>
            {mobileIndustriesOpen && (
              <div className="pl-4 space-y-1">
                {industriesDropdown.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navTo(item.path)}
                    className={`block w-full text-left py-2.5 px-3 rounded flex items-center ${
                      isActive(item.path) ? 'text-[#003366] bg-blue-50 font-semibold' : 'text-gray-600'
                    }`}
                  >
                    <item.icon size={16} className="mr-2" aria-hidden="true" />
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {[
            { path: '/about', label: 'About Us' },
            { path: '/faq', label: 'FAQ' },
            { path: '/blog', label: 'Blog' },
          ].map((item) => (
            <button
              key={item.path}
              onClick={() => navTo(item.path)}
              className={`block w-full text-left font-semibold text-lg py-3 px-2 rounded ${
                isActive(item.path) ? 'text-[#003366] bg-blue-50' : 'text-gray-700'
              }`}
            >
              {item.label}
            </button>
          ))}

          <Button
            onClick={() => navTo('/employers')}
            className="w-full bg-[#003366] hover:bg-[#004488] text-white py-3 mt-2"
          >
            Get Workers Now
          </Button>
        </nav>
      )}
    </header>
  );
};

export default Header;
